import { useEffect } from 'react'
import {
  MAIN_WINDOW_NAME,
  writeFrameDisplay
} from '../../containers/OpenFin/helpers'
import { getListTradingTransactionId } from '../../store/listTrading/selectors'
import { getMarketClosed } from '../../store/market/selectors'
import { useAppSelector } from '../../store/types'
import { getOpenFinParameters } from '../../store/userPreferences/selectors'
import { getSelectedUser, getUserFromUserId } from '../../store/users/selectors'
import { getWatchList } from '../../store/watchList/selectors'
import { getIsAdmin } from '../../store/webSettings/selectors'
import { useOpenFin } from './useOpenFin'

/* The titlebar moved to the OpenFin WindowFrame, which has its own
   unauthenticated store with no WS/Hub. The effects that used to live with it
   need the authed main-view store, so they run here, mounted by App. App's
   openFin handle is passed in rather than calling useOpenFin() again here,
   which would add another nightly-logout effect to App's always-mounted path.
   (SessionContext keeps its own instance, but that effect only closes already-
   closed child windows, so the duplication is idempotent.) */
export const useMainWindowOpenFinEffects = (
  openFin: ReturnType<typeof useOpenFin>
) => {
  const {
    autoLaunch,
    deregisterNotifications,
    finWindowAction,
    isOpenFin,
    openPlatformWindow,
    registerNotifications
  } = openFin
  const isAdmin = useAppSelector(getIsAdmin)
  const marketClosed = useAppSelector(getMarketClosed)
  const selUserId = useAppSelector(getSelectedUser)
  const selectedUser = useAppSelector((s) => getUserFromUserId(s)(selUserId))
  const actingOnBehalfOf =
    selUserId && selectedUser ? selectedUser.userName : ''
  const startupParameters = useAppSelector(getOpenFinParameters)
  const watchlists = useAppSelector(getWatchList)
  const watchlistTransactionId = useAppSelector(getListTradingTransactionId)

  useEffect(() => {
    if (!isOpenFin || fin.me.identity.name !== MAIN_WINDOW_NAME) return
    writeFrameDisplay({ isAdmin, marketClosed, actingOnBehalfOf })
  }, [isOpenFin, isAdmin, marketClosed, actingOnBehalfOf])

  useEffect(() => {
    if (!isOpenFin || fin.me.identity.name !== MAIN_WINDOW_NAME) return
    registerNotifications()
    const app = fin.Application.getCurrentSync()
    const onRunRequested = () => {
      void finWindowAction(null, 'restore')
    }
    void app
      .on('run-requested', onRunRequested)
      .catch((err) => console.error(err))
    return () => {
      void app.removeListener('run-requested', onRunRequested)
      deregisterNotifications()
    }
  }, [isOpenFin])

  useEffect(() => {
    if (!isOpenFin || fin.me.identity.name !== MAIN_WINDOW_NAME) return
    // a list trading window can be created without CreateTradingList being the
    // parent window
    const newList = watchlists?.find(
      (wl) => wl.transactionId === watchlistTransactionId
    )
    if (newList?.id) {
      openPlatformWindow(`ListTrading/${newList.id}`)
    }
  }, [watchlistTransactionId, watchlists, openPlatformWindow, isOpenFin])

  useEffect(() => {
    if (!isOpenFin || fin.me.identity.name !== MAIN_WINDOW_NAME) return
    const interval = autoLaunch(startupParameters)
    return () => clearInterval(interval)
  }, [JSON.stringify(startupParameters)])
}
